import { WarehouseType } from '@elementary/common-ui/types';
import { WAREHOUSE_DISPLAY_NAMES } from '@elementary/common-ui/constants/warehouses';
import { Resource } from 'shared/types';
import {
  CLOUD_TESTS_UNSUPPORTED_WAREHOUSES,
  CLOUD_TESTS_PARTIAL_SUPPORT_WAREHOUSES,
} from 'shared/constants';

const cloudTestTypeMap: Partial<
  Record<
    string,
    | 'freshness_anomalies'
    | 'volume_anomalies'
    | 'data_contract'
    | 'pipeline_task_performance'
  >
> = {
  cloudFreshnessAnomalies: 'freshness_anomalies',
  cloudVolumeAnomalies: 'volume_anomalies',
  cloudDataContract: 'data_contract',
  cloudPipelineTaskPerformance: 'pipeline_task_performance',
};

type CloudTestDisabledReasonArgs = {
  packageName: string;
  testType: string;
  warehouseType?: WarehouseType;
  selectedTableResourcesData: Resource[];
};

export const getCloudTestDisabledReason = ({
  packageName,
  testType,
  warehouseType,
  selectedTableResourcesData,
}: CloudTestDisabledReasonArgs): string | undefined => {
  if (packageName !== 'ELEMENTARY_CLOUD') {
    return undefined;
  }

  const warehouseDisplayName = warehouseType
    ? WAREHOUSE_DISPLAY_NAMES[warehouseType]
    : '';

  const mappedCloudTestType = cloudTestTypeMap[testType];

  if (
    warehouseType &&
    CLOUD_TESTS_UNSUPPORTED_WAREHOUSES.includes(warehouseType)
  ) {
    return `Cloud tests aren't supported for ${warehouseDisplayName} yet.`;
  }

  if (
    warehouseType &&
    CLOUD_TESTS_PARTIAL_SUPPORT_WAREHOUSES.includes(warehouseType)
  ) {
    const lacksIcebergSupport = selectedTableResourcesData.some(
      (resource) =>
        !mappedCloudTestType ||
        !resource.cloud_tests_supported?.[mappedCloudTestType],
    );
    if (lacksIcebergSupport) {
      return `Cloud tests are only supported for Iceberg tables on ${warehouseDisplayName}.`;
    }
  }

  if (!mappedCloudTestType) {
    return undefined;
  }

  const unsupportedResources = selectedTableResourcesData.filter(
    (resource) => !resource.cloud_tests_supported?.[mappedCloudTestType],
  );

  if (unsupportedResources.length === 0) {
    return undefined;
  }

  let reason: string;
  if (unsupportedResources.length === 1) {
    const tableName =
      unsupportedResources[0].fqn ?? unsupportedResources[0].name;
    reason = `The test can not be created for the asset ${tableName}.`;
  } else if (
    unsupportedResources.length === selectedTableResourcesData.length
  ) {
    reason = 'The test can not be created for all the selected assets.';
  } else {
    reason = `The test can not be created for ${unsupportedResources.length} assets.`;
  }

  if (testType === 'cloudPipelineTaskPerformance') {
    reason += ' The supported assets are: models, seeds, and snapshots.';
  } else if (testType === 'cloudDataContract') {
    reason += ' Ephemeral assets are not supported by this test.';
  } else if (
    testType === 'cloudFreshnessAnomalies' ||
    testType === 'cloudVolumeAnomalies'
  ) {
    reason += ' Tables and materialized views are supported by this test.';
  }

  return reason;
};
